import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bookingSettingsApi,
  bookingSettingsQueryKeys,
  roomLimitsApi,
  type BookingSettings,
} from "../../shared/api/bookingSettings";
import { roomQueryKeys, roomsApi } from "../../shared/api/rooms";
import { isLimited, RoomLimitsCard } from "./RoomLimitsCard";
import { checkRoomLimits, DEFAULT_SESSION_LIMIT, roomCeilingFrom } from "./roomLimitRules";
import { slotCount } from "./sessionSlots";
import { BookingReadinessCard } from "./BookingReadinessCard";
import {
  bookingPolicyApi,
  bookingPolicyQueryKeys,
  QUEUE_RESET_OPTIONS,
  QUEUE_SCOPE_OPTIONS,
  type QueueReset,
  type QueueScope,
} from "../../shared/api/bookingPolicy";
import { apiError } from "../../shared/api/client";
import {
  clinicSessionsApi,
  clinicSessionsQueryKeys,
} from "../../shared/api/clinicSessions";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

/** Sentinel used when "Số ngày tối đa" is turned off — effectively no upper cap. */
const NO_MAX = 3650;

function parseDays(text: string): number[] {
  return Array.from(
    new Set(
      text
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((s) => Number.parseInt(s, 10))
        .filter((n) => Number.isFinite(n) && n >= 0 && n <= 365),
    ),
  ).sort((a, b) => a - b);
}

/**
 * #19934 — một câu nói Phạm vi đánh số áp cho số NÀO: phạm vi chỉ quyết dãy STT vào phòng; STT quầy
 * tiếp đón luôn là dãy riêng.
 */
function scopeHint(scope: QueueScope): string {
  return scope === "GENERAL"
    ? "Mọi phòng dùng chung một dãy STT vào phòng. STT quầy tiếp đón là dãy riêng."
    : "Mỗi phòng một dãy STT vào phòng. STT quầy tiếp đón là dãy riêng.";
}

/** Đặt lịch — giới hạn & nhắc lịch. Real /api/v1/clinic/settings/booking. */
export function BookingSettingsPage() {
  const qc = useQueryClient();
  const onlineBooking = useQuery({
    queryKey: [...bookingSettingsQueryKeys.all(), "online-booking"],
    queryFn: () => bookingSettingsApi.getOnlineBooking(),
  });
  const setOnlineBooking = useMutation({
    mutationFn: (enabled: boolean) => bookingSettingsApi.setOnlineBooking(enabled),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...bookingSettingsQueryKeys.all(), "online-booking"],
      });
      qc.invalidateQueries({ queryKey: bookingSettingsQueryKeys.readiness() });
    },
  });
  const q = useQuery({
    queryKey: bookingSettingsQueryKeys.all(),
    queryFn: () => bookingSettingsApi.get(),
  });
  // Buổi khám (Sáng/Chiều/Tối/tùy chỉnh) lấy từ /settings/sessions — nguồn cho các dòng
  // giới hạn theo buổi bên dưới (không còn hardcode Sáng/Chiều).
  const sessionsQ = useQuery({
    queryKey: clinicSessionsQueryKeys.all(),
    queryFn: () => clinicSessionsApi.list(),
  });
  const sessions = (sessionsQ.data ?? []).filter((s) => s.active);
  // #19936 — sức chứa riêng của từng phòng (lượt/ngày).
  const roomLimitsQ = useQuery({
    queryKey: bookingSettingsQueryKeys.roomLimits(),
    queryFn: () => roomLimitsApi.get(),
  });
  const roomsQ = useQuery({
    queryKey: roomQueryKeys.list(true),
    queryFn: () => roomsApi.list(true),
  });
  const branchRooms = (roomsQ.data ?? []).filter(
    (r) => r.branchId === roomLimitsQ.data?.branchId,
  );
  // Giữ dạng chuỗi khi gõ: ô trống = không giới hạn, không tự điền 0.
  const [roomCaps, setRoomCaps] = useState<Record<string, string>>({});
  // Tick "Giới hạn số lượt theo phòng": bật sẵn khi đã có phòng được khai. Bỏ tick rồi Lưu = gỡ hết.
  const [roomLimitOn, setRoomLimitOn] = useState(false);
  /*
   * #19934 — Reset STT và Phạm vi STT vẫn LƯU ở chính sách đặt lịch (/booking-policy), chỉ DỜI chỗ
   * chỉnh về đây, cạnh số bắt đầu: ba thứ cùng trả lời một câu "số thứ tự chạy thế nào". Dùng chung
   * queryKey với trang Luồng & phân công nên hai trang không lệch nhau.
   */
  const policyQ = useQuery({
    queryKey: bookingPolicyQueryKeys.all(),
    queryFn: () => bookingPolicyApi.get(),
  });

  // Số lượng đặt lịch
  const [limited, setLimited] = useState(false);
  // Two quota modes are mutually exclusive — pick one (radio), not both.
  const [quotaMode, setQuotaMode] = useState<"daily" | "perRoom">("daily");
  const [dailyTotal, setDailyTotal] = useState(50);
  // Giới hạn theo buổi, khoá theo mã buổi (clinic_session.code). `enabledCodes` = buổi đang
  // bật giới hạn; `sessionLimitVals` giữ số lượt cho từng buổi (giữ lại khi tắt/bật).
  const [enabledCodes, setEnabledCodes] = useState<Set<string>>(new Set());
  const [sessionLimitVals, setSessionLimitVals] = useState<Record<string, number>>({});
  const [perPersonEnabled, setPerPersonEnabled] = useState(false);
  const [perPersonLimit, setPerPersonLimit] = useState(1);
  const [roomStart, setRoomStart] = useState(1);
  const [registrationStart, setRegistrationStart] = useState(1);
  const [queueReset, setQueueReset] = useState<QueueReset>("DAY");
  const [queueScope, setQueueScope] = useState<QueueScope>("GENERAL");

  // Thời gian đặt lịch
  const [advanceMinEnabled, setAdvanceMinEnabled] = useState(false);
  const [advanceMin, setAdvanceMin] = useState(0);
  const [advanceMaxEnabled, setAdvanceMaxEnabled] = useState(true);
  const [advanceMax, setAdvanceMax] = useState(30);

  // Nhắc lịch
  const [reminderBeforeEnabled, setReminderBeforeEnabled] = useState(false);
  const [reminderBeforeText, setReminderBeforeText] = useState("1");
  const [reminderAfterEnabled, setReminderAfterEnabled] = useState(false);
  const [reminderAfterText, setReminderAfterText] = useState("1");
  const [reminderFollowUpText, setReminderFollowUpText] = useState("1");

  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // #19957 — giới hạn phòng không được vượt "Số lượng đặt lịch — toàn phòng khám" đang nhập.
  const roomCeiling = roomCeilingFrom({
    limited,
    quotaMode,
    dailyTotal,
    enabledCodes,
    sessionLimitVals,
    activeCodes: sessions.map((s) => s.code),
  });
  const roomCheck = checkRoomLimits(
    roomCaps,
    branchRooms.map((r) => r.id),
    roomLimitOn ? roomCeiling : null,
  );

  useEffect(() => {
    const d = q.data;
    if (!d) return;
    setLimited(d.limitEnabled);
    setQuotaMode(d.sessionLimitEnabled && d.dailyTotalLimit == null ? "perRoom" : "daily");
    setDailyTotal(d.dailyTotalLimit ?? 50);
    setSessionLimitVals({ ...(d.sessionLimits ?? {}) });
    setEnabledCodes(new Set(Object.keys(d.sessionLimits ?? {})));
    setPerPersonEnabled(d.perPersonPerDayEnabled);
    setPerPersonLimit(d.perPersonPerDayLimit || 1);
    setRoomStart(d.roomStartNumber ?? 1);
    setRegistrationStart(d.registrationStartNumber ?? 1);
    setAdvanceMinEnabled(d.advanceMinDays > 0);
    setAdvanceMin(d.advanceMinDays);
    setAdvanceMaxEnabled(d.advanceMaxDays < NO_MAX);
    setAdvanceMax(d.advanceMaxDays < NO_MAX ? d.advanceMaxDays : 30);
    setReminderBeforeEnabled(d.reminderBeforeEnabled);
    setReminderBeforeText(d.reminderBeforeDays.join(", ") || "1");
    setReminderAfterEnabled(d.reminderAfterEnabled);
    setReminderAfterText(d.reminderAfterDays.join(", ") || "1");
    setReminderFollowUpText((d.reminderFollowUpDays ?? [1]).join(", ") || "1");
  }, [q.data]);

  useEffect(() => {
    const d = roomLimitsQ.data;
    if (!d) return;
    setRoomCaps(Object.fromEntries(d.limits.map((l) => [l.roomId, String(l.maxPerDay)])));
    setRoomLimitOn(d.limits.length > 0);
  }, [roomLimitsQ.data]);

  useEffect(() => {
    if (!policyQ.data) return;
    setQueueReset(policyQ.data.queueReset);
    setQueueScope(policyQ.data.queueScope);
  }, [policyQ.data]);

  const save = useMutation({
    mutationFn: async () => {
      const body: BookingSettings = {
        limitEnabled: limited,
        dailyTotalLimit: limited && quotaMode === "daily" ? dailyTotal : null,
        sessionLimitEnabled: limited && quotaMode === "perRoom",
        sessionLimits:
          limited && quotaMode === "perRoom"
            ? Object.fromEntries(
                [...enabledCodes].map((c) => [
                  c,
                  // Cùng mặc định với ô đang hiện (25) — trước đây ô hiện 25 mà lưu 0.
                  Math.max(0, sessionLimitVals[c] ?? DEFAULT_SESSION_LIMIT),
                ]),
              )
            : {},
        perPersonPerDayEnabled: perPersonEnabled,
        perPersonPerDayLimit: Math.max(1, perPersonLimit),
        // Phần bù hiển thị cũ (#19934 đã bỏ): không còn ô nhập, gửi lại nguyên giá trị đang lưu.
        startSequenceEnabled: q.data?.startSequenceEnabled ?? false,
        startSequenceNumber: q.data?.startSequenceNumber ?? null,
        advanceMinDays: advanceMinEnabled ? Math.max(0, advanceMin) : 0,
        advanceMaxDays: advanceMaxEnabled ? Math.max(0, advanceMax) : NO_MAX,
        reminderBeforeEnabled,
        reminderBeforeDays: reminderBeforeEnabled ? parseDays(reminderBeforeText) : [],
        reminderAfterEnabled,
        reminderAfterDays: reminderAfterEnabled ? parseDays(reminderAfterText) : [],
        reminderFollowUpDays: parseDays(reminderFollowUpText),
        roomStartNumber: Math.max(1, roomStart),
        registrationStartNumber: Math.max(1, registrationStart),
      };
      const saved = await bookingSettingsApi.update(body);
      // Chỉ ghi khi danh sách đã tải xong: PUT là THAY TOÀN BỘ, nên ghi lúc chưa có dữ liệu là
      // xoá sạch giới hạn phòng mà màn hình chưa từng hiện ra.
      if (roomLimitsQ.data && roomsQ.data) {
        const limits = roomLimitOn
          ? branchRooms
              .filter((r) => isLimited(roomCaps[r.id]))
              .map((r) => ({ roomId: r.id, maxPerDay: Number.parseInt(roomCaps[r.id], 10) }))
          : [];
        const savedRooms = await roomLimitsApi.update(limits);
        qc.setQueryData(bookingSettingsQueryKeys.roomLimits(), savedRooms);
      }
      // Reset/Phạm vi STT nằm trong chính sách đặt lịch: chỉ gửi khi thật sự đổi, và gửi lại nguyên
      // phần còn lại của chính sách để không đè lên thứ trang Luồng & phân công vừa lưu.
      const p = policyQ.data;
      if (p && (p.queueReset !== queueReset || p.queueScope !== queueScope)) {
        const savedPolicy = await bookingPolicyApi.update({ ...p, queueReset, queueScope });
        qc.setQueryData(bookingPolicyQueryKeys.all(), savedPolicy);
      }
      return saved;
    },
    onSuccess: (b) => {
      qc.setQueryData(bookingSettingsQueryKeys.all(), b);
      qc.invalidateQueries({ queryKey: bookingSettingsQueryKeys.readiness() });
      setMsg("Đã lưu cấu hình đặt lịch.");
      setError(null);
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
    },
  });

  const numInput = (
    value: number,
    onChange: (n: number) => void,
    opts: { min?: number; disabled?: boolean; width?: number } = {},
  ) => (
    <Input
      type="number"
      min={opts.min ?? 0}
      value={value}
      disabled={opts.disabled}
      onChange={(e) => onChange(Number.parseInt(e.target.value || "0", 10))}
      className="h-8"
      style={{ width: opts.width ?? 90 }}
    />
  );

  return (
    <div>
      <PageHeader
        title="Đặt lịch — Giới hạn & nhắc lịch"
        description="Giới hạn lượt đặt của toàn phòng khám và của từng bệnh nhân, khoảng thời gian được đặt, số thứ tự bắt đầu và lịch nhắc hẹn."
      />

      {/*
        #18860 — moved here from Lịch nghỉ. Turning the online channel off is a booking-policy
        switch, not a statement about opening hours, so it belongs beside the other booking limits
        rather than beside the closed-days calendar.
      */}
      <Card className="mb-4 max-w-2xl">
        <CardContent className="p-4">
          <label className="flex items-start gap-3">
            <Checkbox
              className="mt-0.5"
              checked={onlineBooking.data ?? true}
              disabled={onlineBooking.isLoading || setOnlineBooking.isPending}
              onCheckedChange={(v) => setOnlineBooking.mutate(v === true)}
            />
            <span>
              <span className="font-medium text-foreground">
                Cho phép bệnh nhân đặt lịch trực tuyến
              </span>
              <span className="block text-sm text-muted-foreground">
                Tắt để tạm dừng nhận đặt lịch online cho chi nhánh này (nhân viên vẫn tạo lịch được).
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      {/* #19939 — báo trước: bật mà không có giờ mở (TC08) + các luật đang áp. */}
      <BookingReadinessCard showRules />

      {q.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
      {q.isError && (
        <p className="text-sm text-destructive">Không tải được cấu hình.</p>
      )}

      {q.data && (
        <div className="grid max-w-2xl gap-4">
          {/* ── Giới hạn toàn phòng khám ───────────────────────── */}
          {/* #19933 — hai phạm vi tách thành hai thẻ: "Không giới hạn" nói về TOÀN phòng khám,
              lượt/người nói về TỪNG bệnh nhân. Chung một thẻ thì ô này trông như con của ô kia. */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Số lượng đặt lịch — toàn phòng khám</CardTitle>
              <p className="text-sm text-muted-foreground">
                Tổng số lượt phòng khám nhận, tính chung cho mọi bệnh nhân.
              </p>
            </CardHeader>
            <CardContent className="grid gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="limitMode"
                  checked={!limited}
                  onChange={() => setLimited(false)}
                  className="h-4 w-4 accent-primary"
                />
                <span>Không giới hạn</span>
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="limitMode"
                  checked={limited}
                  onChange={() => setLimited(true)}
                  className="h-4 w-4 accent-primary"
                />
                <span>Có giới hạn</span>
              </label>

              {limited && (
                <div className="grid gap-3 pl-6">
                  {/* #19977 — hai cách tính loại trừ nhau; nói ra thay vì để người dùng đoán. */}
                  <p className="m-0 text-sm font-medium">Cách tính giới hạn (chọn một)</p>
                  <label className="flex flex-wrap items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="quotaType"
                      checked={quotaMode === "daily"}
                      onChange={() => setQuotaMode("daily")}
                      className="h-4 w-4 accent-primary"
                    />
                    <span>Tổng số lượt đặt trong ngày:</span>
                    {numInput(dailyTotal, setDailyTotal, {
                      min: 0,
                      disabled: quotaMode !== "daily",
                    })}
                    <span>lượt</span>
                  </label>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="quotaType"
                      checked={quotaMode === "perRoom"}
                      onChange={() => setQuotaMode("perRoom")}
                      className="h-4 w-4 accent-primary"
                    />
                    <span>Giới hạn lượt mỗi buổi, cho từng phòng</span>
                  </label>
                  <p className="m-0 pl-6 text-xs text-muted-foreground">
                    Lịch chưa chọn phòng (về quầy) không tính vào đây, chỉ tính vào tổng/ngày.
                  </p>
                  {/* #19935 — đơn vị đang áp theo Hình thức giờ (chỉnh ở trang Buổi khám). */}
                  {policyQ.data && (
                    <p data-testid="limits-time-mode" className="m-0 pl-6 text-xs text-muted-foreground">
                      Bệnh nhân chọn giờ theo:{" "}
                      <strong>
                        {policyQ.data.timeMode === "SLOT"
                          ? `Giờ cụ thể (khung ${policyQ.data.slotMinutes} phút)`
                          : "Buổi"}
                      </strong>
                      {policyQ.data.timeMode === "SLOT"
                        ? " — số lượt tính trên cả buổi chứa giờ hẹn, không phải trên từng khung giờ. Mỗi khung, mỗi phòng nhận 1 bệnh nhân (× số bác sĩ cùng trực)."
                        : " — lịch được tính vào buổi chứa giờ hẹn."}{" "}
                      Đổi ở{" "}
                      <Link to="/settings/sessions" className="underline">
                        Khung giờ nhận lịch
                      </Link>
                      .
                    </p>
                  )}

                  {quotaMode === "perRoom" && (
                    <div className="grid gap-2 pl-6">
                      {sessionsQ.isLoading && (
                        <small className="text-muted-foreground">
                          Đang tải buổi khám…
                        </small>
                      )}
                      {!sessionsQ.isLoading && sessions.length === 0 && (
                        <small className="text-muted-foreground">
                          Chưa có buổi khám nào. Thêm tại{" "}
                          <a href="/settings/sessions" className="text-primary hover:underline">
                            Cài đặt → Khung giờ nhận lịch
                          </a>
                          .
                        </small>
                      )}
                      {sessions.map((s) => {
                        const on = enabledCodes.has(s.code);
                        return (
                          <label
                            key={s.code}
                            className="flex flex-wrap items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={(e) =>
                                setEnabledCodes((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(s.code);
                                  else next.delete(s.code);
                                  return next;
                                })
                              }
                              className="h-4 w-4 accent-primary"
                            />
                            <span className="min-w-14">{s.label}:</span>
                            {/* #19977 — buổi không tick là KHÔNG giới hạn; ô số xám mang 25 trông như
                                đang giới hạn ở 25. */}
                            {on ? (
                              <>
                                {numInput(
                                  sessionLimitVals[s.code] ?? DEFAULT_SESSION_LIMIT,
                                  (n) =>
                                    setSessionLimitVals((prev) => ({ ...prev, [s.code]: n })),
                                  { min: 0 },
                                )}
                                <span>lượt/buổi/phòng</span>
                                {/* #19979 — ở Giờ cụ thể con số dễ bị hiểu là "mỗi khung". */}
                                {policyQ.data?.timeMode === "SLOT" && (
                                  <span className="text-xs text-muted-foreground">
                                    trên{" "}
                                    {slotCount(s.startTime, s.endTime, policyQ.data.slotMinutes)}{" "}
                                    khung
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground">Không giới hạn</span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              ({s.startTime}–{s.endTime})
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

            </CardContent>
          </Card>

          {/* ── Giới hạn theo phòng (#19936) ──────────────────── */}
          <RoomLimitsCard
            rooms={branchRooms}
            loading={roomsQ.isLoading || roomLimitsQ.isLoading}
            enabled={roomLimitOn}
            onEnabledChange={setRoomLimitOn}
            caps={roomCaps}
            onCapChange={(id, v) => setRoomCaps((prev) => ({ ...prev, [id]: v }))}
            slotMode={policyQ.data?.timeMode === "SLOT"}
            errors={roomCheck.perRoom}
            summary={
              roomLimitOn && roomCeiling?.kind === "daily"
                ? {
                    text:
                      roomCheck.sumError ??
                      `Tổng giới hạn các phòng: ${roomCheck.sum} / ${roomCeiling.value} lượt/ngày.`,
                    error: roomCheck.sumError != null,
                  }
                : roomLimitOn && roomCeiling?.kind === "session"
                  ? {
                      text: `Mỗi phòng tối đa ${roomCeiling.value} lượt/ngày (tổng lượt các buổi).`,
                      error: false,
                    }
                  : null
            }
          />

          {/* ── Giới hạn theo từng bệnh nhân ──────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Giới hạn theo từng bệnh nhân</CardTitle>
              <p className="text-sm text-muted-foreground">
                Số lượt một bệnh nhân được đặt trong một ngày — độc lập với giới hạn toàn phòng khám.
              </p>
            </CardHeader>
            <CardContent className="grid gap-3">
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={perPersonEnabled}
                  onChange={(e) => setPerPersonEnabled(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span>Giới hạn lượt/người/ngày:</span>
                {numInput(perPersonLimit, setPerPersonLimit, { min: 1, disabled: !perPersonEnabled })}
                <span>lượt</span>
              </label>
            </CardContent>
          </Card>

          {/* ── Số thứ tự ─────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Số thứ tự</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <p className="m-0 text-sm text-muted-foreground">
                Lịch có phòng nhận STT vào phòng và đến thẳng phòng. Lịch không chọn phòng được đặt
                vào quầy tiếp đón và nhận STT quầy.
              </p>
              {/* #19934 — phòng khám muốn một bộ đếm chung cho mọi phòng phải chọn được điều đó.
                  Ghi chú nói Phạm vi áp cho số nào. */}
              <div className="grid gap-1">
                <label className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="min-w-[180px]">Phạm vi đánh số</span>
                  <select
                    aria-label="Phạm vi đánh số"
                    value={queueScope}
                    onChange={(e) => setQueueScope(e.target.value as QueueScope)}
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {QUEUE_SCOPE_OPTIONS.map((o) => (
                      <option key={o.code} value={o.code}>
                        {o.code === "GENERAL" ? "Chung toàn phòng khám" : "Theo từng phòng"}
                      </option>
                    ))}
                  </select>
                </label>
                <p data-testid="queue-scope-hint" className="m-0 text-xs text-muted-foreground">
                  {scopeHint(queueScope)}
                </p>
              </div>
              <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <legend className="mb-1 font-medium">Đếm lại từ đầu</legend>
                {QUEUE_RESET_OPTIONS.map((o) => (
                  <label key={o.code} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="queueReset"
                      checked={queueReset === o.code}
                      onChange={() => setQueueReset(o.code)}
                      className="h-4 w-4 accent-primary"
                    />
                    <span>{o.code === "DAY" ? "Mỗi ngày" : "Mỗi buổi khám"}</span>
                  </label>
                ))}
              </fieldset>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <span className="min-w-[180px]">Số vào phòng bắt đầu từ</span>
                {numInput(roomStart, setRoomStart, { min: 1 })}
              </label>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <span className="min-w-[180px]">Số tiếp đón bắt đầu từ</span>
                {numInput(registrationStart, setRegistrationStart, { min: 1 })}
              </label>
            </CardContent>
          </Card>

          {/* ── Thời gian đặt lịch ────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Thời gian đặt lịch</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={advanceMinEnabled}
                  onChange={(e) => setAdvanceMinEnabled(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span>Đặt lịch trước</span>
                {numInput(advanceMin, setAdvanceMin, { min: 0, disabled: !advanceMinEnabled })}
                <span>ngày</span>
              </label>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={advanceMaxEnabled}
                  onChange={(e) => setAdvanceMaxEnabled(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span>Số ngày tối đa</span>
                {numInput(advanceMax, setAdvanceMax, { min: 0, disabled: !advanceMaxEnabled })}
                <span>ngày</span>
              </label>
            </CardContent>
          </Card>

          {/* ── Nhắc lịch ─────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Nhắc lịch</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={reminderBeforeEnabled}
                  onChange={(e) => setReminderBeforeEnabled(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span>Nhắc trước lịch hẹn:</span>
                <Input
                  type="text"
                  value={reminderBeforeText}
                  disabled={!reminderBeforeEnabled}
                  onChange={(e) => setReminderBeforeText(e.target.value)}
                  placeholder="1, 2, 3"
                  className="h-8 w-36"
                />
                <span className="text-muted-foreground">ngày</span>
              </label>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={reminderAfterEnabled}
                  onChange={(e) => setReminderAfterEnabled(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <span>Nhắc sau lịch hẹn:</span>
                <Input
                  type="text"
                  value={reminderAfterText}
                  disabled={!reminderAfterEnabled}
                  onChange={(e) => setReminderAfterText(e.target.value)}
                  placeholder="1, 2, 3"
                  className="h-8 w-36"
                />
                <span className="text-muted-foreground">ngày</span>
              </label>
              {/* #19554 — "Nhắc lịch tái khám" là hành động riêng: không có ô tick ở đây, gửi hay
                  không do sự kiện bật ở Cài đặt → Thông báo. Lịch tái khám không nhận nhắc
                  trước lịch hẹn ở dòng trên. */}
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <span className="w-4" aria-hidden />
                <span>Nhắc trước lịch tái khám:</span>
                <Input
                  type="text"
                  value={reminderFollowUpText}
                  onChange={(e) => setReminderFollowUpText(e.target.value)}
                  placeholder="1"
                  className="h-8 w-36"
                />
                <span className="text-muted-foreground">ngày</span>
              </label>
              <small className="text-muted-foreground">
                Danh sách số ngày, cách nhau bằng dấu phẩy (VD: 1, 3, 7). Lịch tái khám do bác sĩ
                hẹn chỉ nhận “Nhắc lịch tái khám” — bật/tắt, kênh và mẫu tin ở Cài đặt → Thông
                báo.
              </small>
            </CardContent>
          </Card>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {roomCheck.hasError && (
            <p data-testid="room-limits-blocked" className="text-sm text-destructive">
              Giới hạn theo phòng đang vượt giới hạn toàn phòng khám — sửa trước khi lưu.
            </p>
          )}
          {msg && <p className="text-sm text-emerald-600">{msg}</p>}

          <div className="flex justify-end">
            <Button
              type="button"
              disabled={save.isPending || roomCheck.hasError}
              onClick={() => {
                setMsg(null);
                setError(null);
                save.mutate();
              }}
            >
              {save.isPending ? "Đang lưu…" : "Lưu thay đổi"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
